import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../hooks/use-toast';

/**
 * Shared kanban Board — native HTML5 drag, optimistic move with rollback,
 * card drawer. Flavor-agnostic: the caller supplies how to read a card's id /
 * stage / title, how to render its face and drawer, and how to persist a move.
 *
 * Props
 *   columns      [{ key, label, color }]            — already filtered/sorted
 *   cards        [any]
 *   cardId       (card) => string
 *   cardStage    (card) => string
 *   cardTitle    (card) => string                   — used in toasts
 *   onMove       (card, toStage) => Promise          — reject => rollback
 *   renderCard   (card) => node                     — card face
 *   renderDrawer ({ card, stage, onClose, onChangeStage }) => node
 *   emptyMsg     string
 *   testIdPrefix string                             — data-testid="{prefix}-board"
 *   openId       string | null                      — optional: ask the Board to open this
 *                                                     card's drawer (e.g. from an alert click)
 *   onOpenChange (id | null) => void                — fired when the drawer opens/closes so the
 *                                                     caller can clear openId and re-open later
 */
export default function Board({
  columns, cards, cardId, cardStage, cardTitle, onMove,
  renderCard, renderDrawer, emptyMsg = 'Drop cards here', testIdPrefix = 'pipeline',
  openId = null, onOpenChange,
}) {
  // Stage placement is held locally so moves can be optimistic and rolled back
  // without mutating the caller's records.
  const [placement, setPlacement] = useState({});
  const [dragging, setDragging] = useState(null);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    const next = {};
    for (const c of cards) next[cardId(c)] = cardStage(c);
    setPlacement(next);
  }, [cards, cardId, cardStage]);

  // External open request (alerts strip etc.) — mirrors into the internal drawer state.
  useEffect(() => { if (openId != null) setDetailId(openId); }, [openId]);
  const closeDetail = useCallback(() => { setDetailId(null); onOpenChange?.(null); }, [onOpenChange]);

  const stageOf = useCallback((card) => placement[cardId(card)] ?? cardStage(card), [placement, cardId, cardStage]);
  const labelOf = (key) => columns.find(c => c.key === key)?.label ?? key;

  const move = useCallback(async (card, toStage) => {
    const id = cardId(card);
    const from = placement[id] ?? cardStage(card);
    if (from === toStage) return false;
    setPlacement(prev => ({ ...prev, [id]: toStage }));
    try {
      await onMove(card, toStage);
      toast({ title: 'Moved', description: `${cardTitle(card)} → ${labelOf(toStage)}` });
      return true;
    } catch (e) {
      setPlacement(prev => ({ ...prev, [id]: from }));
      const code = e?.response?.data?.detail?.code;
      const ghl = code === 'ghl_managed';
      const arive = code === 'arive_managed';
      toast({
        variant: ghl || arive ? 'blocked' : 'destructive',
        title: ghl ? 'Managed in GoHighLevel' : arive ? 'Managed in Arive' : 'Update failed',
        description: ghl
          ? `${cardTitle(card)} is still pre-application — move it in GoHighLevel`
          : arive
            ? `${cardTitle(card)} is managed in Arive — change its stage there`
            : `${cardTitle(card)} stays in ${labelOf(from)}`,
      });
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, cardId, cardStage, cardTitle, onMove, columns]);

  const onDrop = async (toStage) => {
    const card = dragging;
    setDragging(null);
    if (card) await move(card, toStage);
  };

  const detail = detailId != null ? cards.find(c => cardId(c) === detailId) : null;

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-6" data-testid={`${testIdPrefix}-board`}>
        {columns.map((col) => {
          const items = cards.filter(c => stageOf(c) === col.key);
          return (
            <div key={col.key} className="flex-shrink-0 w-72"
              onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(col.key)}
              data-testid={`${testIdPrefix}-col-${col.key}`}>
              <div className="flex items-center justify-between px-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                  <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>{col.label}</div>
                  <span className="text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5" data-testid={`${testIdPrefix}-col-count-${col.key}`}>{items.length}</span>
                </div>
              </div>
              <div className="bg-slate-100 rounded-xl p-2 min-h-[500px]">
                {items.length === 0 && <div className="text-center text-xs text-slate-400 py-6">{emptyMsg}</div>}
                {items.map((c) => {
                  const id = cardId(c);
                  return (
                    <div key={id} draggable
                      onDragStart={() => setDragging(c)}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => setDetailId(id)}
                      className="bg-white rounded-xl p-3 mb-2 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
                      data-testid={`${testIdPrefix}-card-${id}`}>
                      {renderCard(c)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {detail && renderDrawer({
        card: detail,
        stage: stageOf(detail),
        onClose: closeDetail,
        onChangeStage: async (toStage) => { if (await move(detail, toStage)) closeDetail(); },
      })}
    </>
  );
}
